export default {
    data () {
        return {
            isMessageReviewing: false, // 消息是否正在审核中
            messageQueue: [], // 消息队列
            isProcessingQueue: false, // 是否正在处理队列
            currentRequestId: 0 // 请求标识符
        }
    },

    computed: {
        // 队列状态
        queueStatus () {
            return {
                queueLength: this.messageQueue.length,
                isProcessing: this.isProcessingQueue,
                isSending: this.isMessageReviewing,
                pendingCount: this.messageQueue.filter(item => item.status === 'pending').length,
                reviewingCount: this.messageQueue.filter(item => item.status === 'reviewing').length
            }
        }
    },

    methods: {
        /**
         * 触发文本消息审核 - 将消息添加到队列
         * @param {Object} info - 会话信息
         * @param {string} messageContent - 消息内容
         * @param {Array} splitMsgList - 分割后的消息列表
         */
        triggerTextAIReview (info, messageContent, splitMsgList) {
            if (!messageContent || !messageContent.trim()) {
                return
            }

            // 去除HTML标签，获取纯文本内容
            const plainTextContent = messageContent.replace(/<[^>]*>/g, '')

            // 添加消息到队列
            const messageItem = {
                id: Date.now() + Math.random(), // 唯一ID
                content: plainTextContent.trim(),
                originalContent: messageContent,
                splitMsgList,
                info,
                status: 'pending', // pending, reviewing, approved, rejected, failed
                addTime: Date.now()
            }

            this.messageQueue.push(messageItem)

            // 开始处理队列
            this.processQueue()
        },

        /**
         * 处理消息队列
         */
        async processQueue () {
            // 如果正在处理队列，则不重复处理
            if (this.isProcessingQueue) {
                return
            }

            // 如果队列为空，则不处理
            if (this.messageQueue.length === 0) {
                return
            }

            const startRequestId = this.currentRequestId
            this.isProcessingQueue = true
            this.isMessageReviewing = true

            try {
                while (this.messageQueue.length > 0) {
                    // 检查是否被中断（用户切换了会话）
                    if (startRequestId !== this.currentRequestId) {
                        console.warn('队列处理被中断，用户已切换会话')
                        break
                    }

                    const currentMessage = this.messageQueue[0]

                    // 只处理待处理的消息
                    if (currentMessage.status !== 'pending') {
                        this.messageQueue.shift()
                        continue
                    }

                    // 开始审核当前消息
                    if (currentMessage.type === 'image') {
                        await this.reviewImageMessage(currentMessage)
                    } else {
                        await this.reviewTextMessage(currentMessage)
                    }

                    // 处理完成后移除队列中的消息
                    this.messageQueue.shift()
                }
            } catch (error) {
                console.error('处理消息队列时发生错误:', error)
            } finally {
                // 只有在没有被中断的情况下才重置状态
                if (startRequestId === this.currentRequestId) {
                    this.isProcessingQueue = false
                    this.isMessageReviewing = false
                }
            }
        },

        /**
         * 审核单条消息
         * @param {Object} messageItem 消息项
         */
        async reviewTextMessage (messageItem) {
            try {
                messageItem.status = 'reviewing'

                // 构建审核所需的消息格式
                const formattedMessages = this.buildFormattedMessages(messageItem.content)

                let messageProcessed = false

                // 设置超时处理
                const timeoutId = setTimeout(() => {
                    if (messageProcessed) {
                        return
                    }

                    messageProcessed = true
                    console.warn('AI审核超时，允许发送消息:', messageItem.content)

                    // 超时情况下直接发送消息
                    this.sendQueueMessage(messageItem)
                }, 5000) // 5秒超时

                try {
                    // 发起AI审核请求
                    const reviewResult = await this.$api.reviewMessageAPI({
                        groupId: messageItem.info.targetId,
                        messages: formattedMessages
                    })

                    // 清除超时器
                    clearTimeout(timeoutId)

                    // 检查请求是否过期
                    if (messageProcessed) {
                        console.warn('忽略过期的审核结果')
                        return
                    }

                    messageProcessed = true

                    // 处理审核结果
                    if (!reviewResult.success) {
                        messageItem.status = 'rejected'
                        console.warn('消息审核未通过:', reviewResult.info || '消息审核未通过')
                    } else if (reviewResult.data.violation) {
                        messageItem.status = 'rejected'
                        console.warn('消息违规，禁止发送:', messageItem.content)
                    } else {
                        messageItem.status = 'approved'
                        // 审核通过，发送消息
                        this.sendQueueMessage(messageItem)
                    }
                } catch (error) {
                    clearTimeout(timeoutId)

                    // 检查请求是否过期
                    if (messageProcessed) {
                        console.warn('忽略过期的审核错误')
                        return
                    }

                    messageProcessed = true
                    messageItem.status = 'failed'

                    console.error('AI审核失败:', error)

                    // 审核失败时也发送消息（降级处理）
                    this.sendQueueMessage(messageItem)
                }
            } catch (error) {
                messageItem.status = 'failed'
                console.error('消息审核过程发生异常:', error)

                // 异常情况下也发送消息（降级处理）
                this.sendQueueMessage(messageItem)
            }
        },

        /**
         * 构建用于AI审核的消息格式
         * @param {string} currentMessageContent 当前消息内容
         * @returns {Array} 格式化后的消息数组
         */
        buildFormattedMessages (currentMessageContent) {
            // 获取最近的4条自己发送的文本消息
            const formattedMessages = [...this.history].reverse().reduce((result, msg) => {
                // 只处理自己发送的文本消息
                if (msg.senderUserId != this.userinfo.id || msg.messageType !== 'RC:TxtMsg') {
                    return result
                }

                // 最多收集4条消息
                if (result.length >= 4) {
                    return result
                }

                result.push({
                    nickname: this.userinfo.nickname,
                    type: msg.messageType,
                    userId: this.userinfo.id,
                    content: msg.content.content || ''
                })

                return result
            }, []).reverse()

            // 添加当前正要发送的消息
            if (currentMessageContent) {
                formattedMessages.push({
                    nickname: this.userinfo.nickname,
                    type: 'RC:TxtMsg',
                    userId: this.userinfo.id,
                    content: currentMessageContent
                })
            }

            return formattedMessages
        },

        /**
         * 发送队列中的消息
         * @param {Object} messageItem 消息项
         */
        async sendQueueMessage (messageItem) {
            try {
                // 检查当前会话是否匹配，防止发送到错误的会话
                if (!this.customerInfo || this.customerInfo.targetId !== messageItem.info.targetId) {
                    console.warn('会话已切换，取消发送队列消息')
                    return
                }

                const curConversation = {
                    conversationType: messageItem.info.conversationType,
                    targetId: messageItem.info.targetId
                }

                let _tempMsgItem = null
                for (let index = 0; index < messageItem.splitMsgList.length; index++) {
                    _tempMsgItem = messageItem.splitMsgList[index]
                    // 发送文本消息
                    await this.sendSimple(curConversation, _tempMsgItem)
                }
            } catch (error) {
                console.error('发送消息失败:', error)
            }
        },

        /**
         * 处理消息审核逻辑（旧方法，保持兼容性）
         * @param {Object} info - 会话信息
         * @param {string} messageContent - 消息内容
         * @param {Array} splitMsgList - 分割后的消息列表
         * @returns {boolean} 审核是否通过
         */
        handleMessageReview (info, messageContent, splitMsgList) {
            // 使用新的队列方式
            this.triggerTextAIReview(info, messageContent, splitMsgList)
            return true // 立即返回true，不阻塞用户输入
        },

        /**
         * 清空队列并中断正在进行的请求
         */
        clearQueue () {
            // 中断正在进行的请求（通过增加requestId来标记请求过期）
            this.currentRequestId++

            // 清空队列
            this.messageQueue.length = 0
            this.isProcessingQueue = false
            this.isMessageReviewing = false

            // 恢复编辑器状态
            if (this.$refs.mwQuillEditor && this.$refs.mwQuillEditor.quill) {
                this.$refs.mwQuillEditor.quill.enable(!this.groupData?.isBan)
                this.$refs.mwQuillEditor.quill.root.dataset.placeholder = '请输入...'
            }
        },

        /**
     * 禁用编辑器
     * @param {string} placeholder - 占位符文本
     */
        disableEditor (placeholder = '消息审核中...') {
            if (this.$refs.mwQuillEditor && this.$refs.mwQuillEditor.quill) {
                this.$refs.mwQuillEditor.quill.enable(false)
                this.$refs.mwQuillEditor.quill.root.dataset.placeholder = placeholder
            }
        },

        /**
     * 恢复编辑器状态
     */
        restoreEditor () {
            this.isMessageReviewing = false
            if (this.$refs.mwQuillEditor && this.$refs.mwQuillEditor.quill) {
                this.$refs.mwQuillEditor.quill.enable(!this.groupData?.isBan)
                this.$refs.mwQuillEditor.quill.root.dataset.placeholder = '请输入...'
                // 添加这一行，让编辑器重新获得焦点
                this.$nextTick(() => {
                    this.$refs.mwQuillEditor.quill.focus()
                })
            }
        },

        /**
     * 检查是否需要进行消息审核
     * @returns {boolean} 是否需要审核
     */
        shouldReviewMessage () {
            return this.groupType === 0
        },

        /**
         * 检查是否可以发送消息
         * @returns {boolean} 是否可以发送
         */
        canSendMessage () {
            // 队列模式下只检查群组是否被禁言，不检查审核状态
            if (this.groupData?.isBan) {
                this.$message.error('该群已被禁言，暂时无法发送消息')
                return false
            }
            return true
        },

        /**
         * 触发图片审核 - 将图片添加到队列
         * @param {string} imageUrl - 图片URL
         * @param {File} file - 图片文件
         */
        triggerImageAIReview (imageUrl, file) {
            // 添加图片到队列
            const messageItem = {
                id: Date.now() + Math.random(), // 唯一ID
                content: imageUrl,
                file,
                type: 'image',
                status: 'pending', // pending, reviewing, approved, rejected, failed
                addTime: Date.now()
            }

            this.messageQueue.push(messageItem)

            // 开始处理队列
            this.processQueue()
        },

        /**
         * 审核单条图片消息
         * @param {Object} messageItem 消息项
         */
        async reviewImageMessage (messageItem) {
            try {
                messageItem.status = 'reviewing'

                let messageProcessed = false

                // 设置超时处理
                const timeoutId = setTimeout(() => {
                    if (messageProcessed) {
                        return
                    }

                    messageProcessed = true
                    console.warn('图片审核超时，允许发送图片:', messageItem.content)

                    // 超时情况下直接发送图片
                    this.processSendImage(messageItem.content, messageItem.file)
                }, 5000) // 5秒超时

                try {
                    // 发起图片审核请求
                    const reviewResult = await this.$api.reviewMessageAPI({
                        groupId: this.customerInfo.targetId,
                        messages: [
                            {
                                content: messageItem.content,
                                nickname: this.userinfo.nickname,
                                type: 'RC:ImgMsg',
                                userId: this.userinfo.id
                            }
                        ]
                    })

                    // 清除超时器
                    clearTimeout(timeoutId)

                    // 检查请求是否过期
                    if (messageProcessed) {
                        console.warn('忽略过期的图片审核结果')
                        return
                    }

                    messageProcessed = true

                    // 处理审核结果
                    if (!reviewResult.success) {
                        messageItem.status = 'rejected'
                        console.warn('图片审核未通过:', reviewResult.info || '图片审核未通过')
                    } else if (reviewResult.data.violation) {
                        messageItem.status = 'rejected'
                        console.warn('图片违规，禁止发送:', messageItem.content)
                    } else {
                        messageItem.status = 'approved'
                        // 审核通过，发送图片
                        await this.processSendImage(messageItem.content, messageItem.file)
                    }
                } catch (error) {
                    clearTimeout(timeoutId)

                    // 检查请求是否过期
                    if (messageProcessed) {
                        console.warn('忽略过期的图片审核错误')
                        return
                    }

                    messageProcessed = true
                    messageItem.status = 'failed'

                    console.error('图片审核失败:', error)

                    // 审核失败时也发送图片（降级处理）
                    await this.processSendImage(messageItem.content, messageItem.file)
                }
            } catch (error) {
                messageItem.status = 'failed'
                console.error('图片审核过程发生异常:', error)

                // 异常情况下也发送图片（降级处理）
                await this.processSendImage(messageItem.content, messageItem.file)
            }
        },

        /**
         * 处理图片审核逻辑（旧方法，保持兼容性）
         * @param {string} imageUrl - 图片URL
         * @param {File} file - 图片文件
         * @returns {boolean} 审核是否通过
         */
        handleImageReview (imageUrl, file) {
            // 使用新的队列方式
            this.triggerImageAIReview(imageUrl, file)
            return true // 立即返回true，不阻塞用户输入
        },

        /**
     * 处理发送图片
     * @param {string} imageUrl - 图片URL
     * @param {File} file - 图片文件
     */
        async processSendImage (imageUrl, file) {
            try {
                // 导入需要的工具函数
                const { comporessImgToTargetSize_IMG_MSG_CONTENT, imageUrlToBase64, fileToBase64 } = await import('~/utils/index')

                let smallPicBase64
                const { res: smallPic, success: smallPicSuccess } = await comporessImgToTargetSize_IMG_MSG_CONTENT(file, 8 * 1024)
                if (!smallPicSuccess) {
                    smallPicBase64 = await imageUrlToBase64(`${imageUrl}?x-oss-process=image/resize,l_100`)
                } else {
                    smallPicBase64 = await fileToBase64(smallPic)
                }
                this.imageUrl = imageUrl
                this.send(file, smallPicBase64)
            } catch (error) {
                console.error('处理图片发送失败:', error)
            }
        }
    }
}
